import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/layout/PageHeader';
import { ContentWrapper } from '@/components/layout/ContentWrapper';
import { useCreditBalance } from '@/hooks/useCreditBalance';
import {
  DollarSign, CreditCard, Shield, CheckCircle, Calculator,
  ArrowRight, RefreshCw, AlertCircle, Wallet,
} from 'lucide-react';

const PurchaseCredits: React.FC = () => {
  const { balance, packages, isLoading, purchaseCredits } = useCreditBalance();
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [isPurchasing, setIsPurchasing] = useState(false);

  const selectedPkg = packages.find((p) => p.id === selectedPackage);

  const customCredits = parseInt(customAmount) || 0;
  const customPrice = customCredits * 0.095;

  const orderSummary = useMemo(() => {
    if (selectedPkg) {
      return { credits: selectedPkg.credits, price: selectedPkg.price, name: selectedPkg.name };
    }
    if (customCredits >= 100) {
      return { credits: customCredits, price: customPrice, name: 'Custom Amount' };
    }
    return null;
  }, [selectedPkg, customCredits, customPrice]);

  const handlePurchase = async () => {
    if (!orderSummary) return;
    setIsPurchasing(true);
    await new Promise((r) => setTimeout(r, 2000));
    setIsPurchasing(false);
    alert(`Purchase successful! ${orderSummary.credits.toLocaleString()} credits added.`);
  };

  return (
    <ContentWrapper>
      <PageHeader
        title="Purchase Credits"
        subtitle="Select a credit package or enter a custom amount"
        breadcrumbs={[
          { label: 'Account & Billing' },
          { label: 'Purchase Credits', isActive: true },
        ]}
      />

      {/* Current Balance Banner */}
      <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200 mb-8">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wallet className="h-5 w-5 text-blue-600" />
            <span className="text-sm text-gray-700">Current Balance:</span>
            <span className="text-lg font-bold text-gray-900">{balance.available.toLocaleString()} credits</span>
          </div>
          <span className="text-sm text-gray-500">~{balance.estimatedWeeksRemaining} weeks remaining</span>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: Packages + Custom */}
        <div className="lg:col-span-2 space-y-8">
          {/* Credit Packages */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Credit Packages</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {packages.map((pkg) => (
                <div
                  key={pkg.id}
                  onClick={() => { setSelectedPackage(pkg.id); setCustomAmount(''); }}
                  className={`relative cursor-pointer rounded-xl border-2 p-6 transition-all ${
                    selectedPackage === pkg.id
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200 shadow-lg'
                      : pkg.popular
                      ? 'border-blue-300 hover:border-blue-400 hover:shadow-md'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-blue-600 text-white text-xs">MOST POPULAR</Badge>
                    </div>
                  )}
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                    <div className="mb-4">
                      <span className="text-3xl font-bold text-gray-900">${pkg.price.toFixed(2)}</span>
                    </div>
                    <div className="space-y-2 text-sm text-gray-600 mb-6">
                      <p><strong>{pkg.credits.toLocaleString()}</strong> Credits</p>
                      <p>(${pkg.pricePerCredit.toFixed(3)} each)</p>
                      <p>Valid {pkg.validity}</p>
                      <p>95% Accuracy Guarantee</p>
                      {pkg.savings && <p className="text-green-600 font-medium">Save {pkg.savings}</p>}
                    </div>
                    <Button
                      className={`w-full ${
                        selectedPackage === pkg.id
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                      }`}
                    >
                      {selectedPackage === pkg.id ? '✓ Selected' : 'Select Package'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Amount */}
          <Card className="bg-white border border-gray-200 shadow-sm">
            <CardHeader className="border-b border-gray-100">
              <CardTitle className="text-base text-gray-900 flex items-center gap-2">
                <Calculator className="h-5 w-5 text-gray-500" /> Custom Credit Amount
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-sm text-gray-500 mb-4">
                Need a different amount? Enter any number of credits (minimum 100).
                Custom pricing: $0.095 per credit.
              </p>
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of Credits</label>
                  <input
                    type="number"
                    min="100"
                    step="100"
                    value={customAmount}
                    onChange={(e) => { setCustomAmount(e.target.value); setSelectedPackage(null); }}
                    placeholder="e.g. 2500"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Cost</label>
                  <div className="border border-gray-200 rounded-lg px-4 py-2 bg-gray-50 text-lg font-semibold text-gray-900">
                    {customCredits >= 100 ? `$${customPrice.toFixed(2)}` : '--'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right: Order Summary */}
        <div className="space-y-6">
          <Card className="bg-white border border-gray-200 shadow-sm sticky top-8">
            <CardHeader className="border-b border-gray-100">
              <CardTitle className="text-base text-gray-900">Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {orderSummary ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">{orderSummary.name}</span>
                    <span className="font-semibold text-gray-900">{orderSummary.credits.toLocaleString()} credits</span>
                  </div>
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex justify-between items-center text-lg">
                      <span className="font-medium text-gray-900">Total</span>
                      <span className="font-bold text-gray-900">${orderSummary.price.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Payment Method */}
                  <div className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <CreditCard className="h-5 w-5 text-gray-500" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">Visa ending in 4242</p>
                        <p className="text-xs text-gray-500">Default payment method</p>
                      </div>
                      <button className="text-xs text-blue-600 hover:underline">Change</button>
                    </div>
                  </div>

                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12"
                    onClick={handlePurchase}
                    disabled={isPurchasing}
                  >
                    {isPurchasing ? (
                      <span className="flex items-center gap-2">
                        <RefreshCw className="h-4 w-4 animate-spin" /> Processing...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Purchase {orderSummary.credits.toLocaleString()} Credits <ArrowRight className="h-4 w-4" />
                      </span>
                    )}
                  </Button>

                  {/* Security Badge */}
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-600 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <Shield className="w-4 h-4" />
                    <span>Backed by Stripe Security + ClaimsIQ Guarantee</span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <DollarSign className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">Select a package or enter a custom amount to see your order summary.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Accuracy Guarantee */}
          <Card className="bg-green-50 border border-green-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-green-800 mb-1">95% Accuracy Guarantee</p>
                  <p className="text-green-700">
                    If our matching accuracy falls below 95%, you receive
                    automatic credit refunds for inaccurate results.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </ContentWrapper>
  );
};

export default PurchaseCredits;